// Review keys: every reviewable item has a stable key. Absent from the database = needs_review.
//   vf|root|tense|person|number|form   verb form (also अस)
//   kf|root|kind|form                  कृदन्त form
//   nf|stem|case|number|form           noun form
//   pf|table|case|number|form          pronoun form
//   lex|dhatu|पढ, lex|noun|पुरिस, lex|avyaya|अत्थ, lex|number|…, lex|irregular|…   dictionary entries
//   lesson|id, story|id, sentence|id, ui|key, dhvani|sa|pra, sandhi|id, samas|id, lit|text

export type ReviewEntity =
    | 'verb_form'
    | 'kridanta_form'
    | 'noun_form'
    | 'pronoun_form'
    | 'lexicon'
    | 'lesson'
    | 'story'
    | 'sentence'
    | 'ui'
    | 'sound_change'
    | 'sandhi'
    | 'samas'
    | 'literal'
    | 'audio'

const PREFIX: Record<string, ReviewEntity> = {
    vf: 'verb_form',
    kf: 'kridanta_form',
    nf: 'noun_form',
    pf: 'pronoun_form',
    lex: 'lexicon',
    lesson: 'lesson',
    story: 'story',
    sentence: 'sentence',
    ui: 'ui',
    dhvani: 'sound_change',
    sandhi: 'sandhi',
    samas: 'samas',
    lit: 'literal',
    audio: 'audio'
}

export const ENTITY_LABEL: Record<ReviewEntity, { hi: string, en: string }> = {
    verb_form: { hi: 'क्रिया-रूप', en: 'Verb forms' },
    kridanta_form: { hi: 'कृदन्त-रूप', en: 'Participle forms' },
    noun_form: { hi: 'संज्ञा-रूप', en: 'Noun forms' },
    pronoun_form: { hi: 'सर्वनाम-रूप', en: 'Pronoun forms' },
    lexicon: { hi: 'शब्दकोश प्रविष्टियाँ', en: 'Dictionary entries' },
    lesson: { hi: 'पाठ', en: 'Lessons' },
    story: { hi: 'कथाएँ', en: 'Stories and dialogues' },
    sentence: { hi: 'उदाहरण वाक्य', en: 'Example sentences' },
    ui: { hi: 'इंटरफ़ेस के प्राकृत शब्द', en: 'Interface Prakrit strings' },
    sound_change: { hi: 'ध्वनि-परिवर्तन युग्म', en: 'Sound-change pairs' },
    sandhi: { hi: 'सन्धि उदाहरण', en: 'Sandhi examples' },
    samas: { hi: 'समास उदाहरण', en: 'Compound examples' },
    literal: { hi: 'पूर्व-परिचित वाक्यांश', en: 'Pre-taught phrases' },
    audio: { hi: 'रिकॉर्डिंग', en: 'Recordings' }
}

export const entityOf = (key: string): { entity: ReviewEntity, lemma: string } => {
    const parts = key.split('|')
    const entity = PREFIX[parts[0]] ?? 'literal'

    if (entity === 'lexicon') return { entity, lemma: `${parts[1]}|${parts[2] ?? ''}` }

    return { entity, lemma: parts[1] ?? '' }
}

export const isReviewKey = (key: unknown): key is string => typeof key === 'string' && key.length > 2 && key.length < 400 && key.split('|')[0] in PREFIX
